import {Box, Flex, Skeleton} from '@chakra-ui/react';
import {Slider} from 'components/shared';
import {useDesktop} from 'hooks';
import {FC, Fragment} from 'react';
import GTMService from 'services/ga4';
import {IBannersResponse} from 'types';
import LinkUI from 'ui/LinkUI';
import NextImageUI from 'ui/NextImage';

interface IProps {
  banners: IBannersResponse[];
  isLoading?: boolean;
}

const customSliderStyles = {
  '.slider-arrow-right': {
    right: '-7px !important',
    top: ['calc(50% - 40px)'],
  },
  '.slider-arrow-left': {
    left: '-7px !important',
    top: ['calc(50% - 40px)'],
  },
};

export const Banners: FC<IProps> = ({banners, isLoading}) => {
  const isDesktop = useDesktop();
  const imgSize = isDesktop ? {width: 1440, height: 228} : {width: 750, height: 420};

  if (!banners?.length && !isLoading) return null;

  return (
    <Box px={['auto', '50px', '150px']} mt={['20px', '30px']} pb="5px" sx={customSliderStyles} mx="0 auto" h="full">
      {isLoading ? (
        <Flex w="full" justifyContent="center" px="15px" pb="6px">
          <Skeleton
            w={['380px', '100%']}
            borderRadius="16px"
            h="full"
            sx={{
              aspectRatio: [`${imgSize.width} / ${imgSize.height}`],
            }}
          />
        </Flex>
      ) : (
        <Slider slidesToShow={1} dots={banners.length > 1} autoplaySpeed={5000} autoplay>
          {banners.map((banner, index) => {
            const isBannerClickable = banner.url || banner.source_slug.length;
            const img = isDesktop ? banner.image_thumbnail : banner.image_mobile_thumbnail;
            // used by next/image to display properly aspect ratio
            if (!img) return null;

            const handleBannerClick = () => {
              if (!isBannerClickable) return;
              GTMService.selectPromotion(banner);
            };
            const bannerChild = () => (
              <Box cursor={isBannerClickable ? 'pointer' : 'default'} px="15px" onClick={handleBannerClick}>
                <Flex w="100%" justifyContent="center">
                  <Box w={['380px', '100%']} overflow="hidden">
                    <NextImageUI
                      src={img}
                      priority={index === 0}
                      loading={index === 0 ? 'eager' : 'lazy'}
                      style={{
                        borderRadius: '16px',
                      }}
                      width={imgSize.width}
                      height={imgSize.height}
                    />
                  </Box>
                </Flex>
              </Box>
            );

            return isBannerClickable ? (
              <LinkUI key={banner.id} href={banner.url}>
                {bannerChild()}
              </LinkUI>
            ) : (
              <Fragment key={banner.id}>{bannerChild()}</Fragment>
            );
          })}
        </Slider>
      )}
    </Box>
  );
};
